import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  chakra,
  Collapse,
  Flex,
  Grid,
  Icon,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  VisuallyHidden,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiArrowDown, FiChevronDown, FiRotateCcw, FiSearch, FiShoppingBag } from "react-icons/fi";

import {
  DataTable,
  EmptyState,
  MotionReveal,
  Pagination,
  SearchField,
  SectionCard,
  SegmentedControl,
  StatCard,
  StatSkeleton,
  TableSkeleton,
} from "../ui";
import { ease, reducedTransition, spring, useReducedMotion } from "../../theme";
import { SpendAnalysisItem } from "../../types/analysisDataTypes";
import { formatAmount, formatCompact } from "../../utils/formatNumber";

const ITEMS_PER_PAGE = 8;
const TOP_ITEMS = 10;

type Period = "this_month_sale" | "last_month_sale" | "last_6_month_sale";

const PERIODS: Array<{ key: Period; label: string }> = [
  { key: "this_month_sale", label: "This month" },
  { key: "last_month_sale", label: "Last month" },
  { key: "last_6_month_sale", label: "Last 6 months" },
];

const MotionBox = motion(Box);

// "12.3%", and "< 0.1%" rather than a misleading "0.0%"
const formatShare = (share: number) => (share > 0 && share < 0.1 ? "< 0.1%" : `${share.toFixed(1)}%`);

// Three totals: 3-up from xl; between md and xl the third card takes the full row instead of a half-width orphan
// (the nowrap amounts do not fit a 3-up row there).
const totalsColumn = (index: number) =>
  index === PERIODS.length - 1 ? { md: "1 / -1", xl: "auto" } : undefined;

interface SpendAnalysisProps {
  items: SpendAnalysisItem[] | undefined;
  isPending: boolean;
  isError: boolean;
  currency: string;
  // Optional: shows a Retry button in the error state when the parent passes a refetch
  onRetry?: () => void;
}

const SpendAnalysis: React.FC<SpendAnalysisProps> = ({
  items,
  isPending,
  isError,
  currency,
  onRetry,
}) => {
  const reduce = useReducedMotion();
  // The three segment labels only fit a 375px card at the small type size (height is raised to 44px below)
  const segmentSize = useBreakpointValue<"sm" | "md">({ base: "sm", md: "md" }) ?? "md";
  const [period, setPeriod] = useState<Period>("last_6_month_sale");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  // Top-10 item whose breakdown is open (one at a time)
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const allItemsRef = useRef<HTMLDivElement>(null);

  const totals = useMemo(
    () =>
      PERIODS.map(({ key, label }) => ({
        key,
        label,
        total: (items ?? []).reduce((sum, item) => sum + item[key], 0),
        itemCount: (items ?? []).filter((item) => item[key] > 0).length,
      })),
    [items]
  );

  const topItems = useMemo(
    () =>
      (items ?? [])
        .filter((item) => item[period] > 0)
        .sort((a, b) => b[period] - a[period])
        .slice(0, TOP_ITEMS),
    [items, period]
  );

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (items ?? [])
      .filter(
        (item) =>
          !term ||
          item.item_name.toLowerCase().includes(term) ||
          item.description.toLowerCase().includes(term)
      )
      .sort((a, b) => b[period] - a[period]);
  }, [items, period, searchTerm]);

  useEffect(() => setPage(1), [period, searchTerm]);

  // Close the breakdown when its item leaves the Top 10, so it does not reappear open on a later period switch
  useEffect(() => {
    if (expandedItemId !== null && !topItems.some((item) => item.item_id === expandedItemId)) {
      setExpandedItemId(null);
    }
  }, [topItems, expandedItemId]);

  // A refetch can shrink the list under the current page
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const currentItems = filteredItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const periodLabel = PERIODS.find(({ key }) => key === period)?.label;
  const maxSpend = topItems[0]?.[period] ?? 0;
  const periodTotal = totals.find(({ key }) => key === period)?.total ?? 0;
  const withCurrency = (amount: number) => `${currency ? `${currency} ` : ""}${formatAmount(amount)}`;

  // "Show in table": narrow the All items table to this item, bring it into view and move focus there, so the
  // next Tab reaches the search field and table (not the Top 10 list) and screen readers announce the filtered heading.
  // Focusing the heading block rather than the search input keeps the on-screen keyboard closed on phones.
  const showInTable = (item: SpendAnalysisItem) => {
    setSearchTerm(item.item_name);
    requestAnimationFrame(() => {
      allItemsRef.current?.focus({ preventScroll: true });
      allItemsRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    });
  };

  const hasItems = Boolean(items?.length);
  // Currency may still be "" while the financial details load
  const columnLabel = (label: string) => (currency ? `${label} (${currency})` : label);
  const tableColumns = ["Item", ...PERIODS.map(({ label }) => columnLabel(label))];

  const renderContent = () => {
    if (isPending) {
      return (
        <>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={4} mb={6}>
            {PERIODS.map(({ key }, index) => (
              <Box key={key} gridColumn={totalsColumn(index)}>
                <StatSkeleton />
              </Box>
            ))}
          </SimpleGrid>
          <TableSkeleton columns={tableColumns} rows={5} size="sm" />
        </>
      );
    }

    // After a failed refetch keep showing the data we already have
    if (isError && !items) {
      return (
        <Alert status="error" alignItems="center" flexWrap="wrap" gap={2}>
          <AlertIcon />
          <AlertDescription flex="1" minW="16rem">
            We couldn't load your spend analysis. Please try again later.
          </AlertDescription>
          {onRetry && (
            <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={onRetry}>
              Retry
            </Button>
          )}
        </Alert>
      );
    }

    if (!items?.length) {
      return (
        <EmptyState
          icon={FiShoppingBag}
          title="No purchases in the last 6 months"
          description="Your spend per item appears here once you start ordering."
        />
      );
    }

    return (
      <>
        {/* Clicking a total selects its period, same state as the segmented control; the selected card carries the ring */}
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={4} mb={6}>
          {totals.map(({ key, label, total, itemCount }, index) => (
            <Box key={key} gridColumn={totalsColumn(index)}>
              <MotionReveal delay={index * 0.04} style={{ height: "100%" }}>
                <StatCard
                  variant="tile"
                  tone="brand"
                  label={label}
                  unit={currency || undefined}
                  value={total}
                  format={formatAmount}
                  caption={
                    key === "last_6_month_sale"
                      ? `About ${withCurrency(total / 6)} a month · ${itemCount} items`
                      : `${itemCount} items bought`
                  }
                  isSelected={period === key}
                  onClick={() => setPeriod(key)}
                  h="full"
                />
              </MotionReveal>
            </Box>
          ))}
        </SimpleGrid>

        <Flex align="baseline" justify="space-between" gap={3} wrap="wrap" mb={3}>
          <Text as="h3" textStyle="h3" color="text.primary">
            Top {TOP_ITEMS} items
          </Text>
          <Text textStyle="caption" color="text.muted">
            {periodLabel} · by spend
          </Text>
        </Flex>

        {topItems.length === 0 ? (
          <Text textStyle="body.sm" color="text.muted" py={4}>
            No purchases in this period.
          </Text>
        ) : (
          <Box
            as="ol"
            role="list"
            listStyleType="none"
            aria-label={`Top ${TOP_ITEMS} items by spend, ${periodLabel}`}
            m={0}
            p={0}
          >
            {topItems.map((item, index) => {
              const share = maxSpend > 0 ? (item[period] / maxSpend) * 100 : 0;
              const isOpen = expandedItemId === item.item_id;
              const panelId = `spend-item-${item.item_id}`;
              return (
                <MotionBox
                  as="li"
                  key={item.item_id}
                  layout="position"
                  transition={reduce ? reducedTransition : spring.snappy}
                  mx={-2}
                >
                  <Tooltip
                    hasArrow
                    openDelay={300}
                    placement="top"
                    isDisabled={isOpen}
                    label={`${item.description} (${item.item_name}): ${withCurrency(item[period])}`}
                    // The button name already has the description and amount: the tooltip's description adds only the code
                    aria-label={`Item code ${item.item_name}`}
                  >
                    <chakra.button
                      type="button"
                      display="block"
                      position="relative"
                      w="full"
                      textAlign="left"
                      px={2}
                      py={2}
                      borderRadius="md"
                      cursor="pointer"
                      data-group
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setExpandedItemId(isOpen ? null : item.item_id)}
                      bg={isOpen ? "navy.50" : "transparent"}
                      transition="background-color 160ms"
                      _hover={{ bg: "navy.50" }}
                      // Above the next item's button, whose background would otherwise cover the ring's bottom edge
                      _focusVisible={{ boxShadow: "outline", outline: "none", zIndex: 1 }}
                    >
                      <Grid
                        as="span"
                        templateColumns={{
                          base: "auto minmax(0, 1fr) auto auto",
                          md: "auto minmax(0, 2fr) minmax(0, 3fr) 5rem auto",
                        }}
                        alignItems="center"
                        columnGap={{ base: 3, md: 4 }}
                        rowGap={1}
                      >
                        <Text
                          as="span"
                          textStyle="display.sm"
                          fontWeight={600}
                          color={isOpen ? "navy.900" : "sand.400"}
                          w="2ch"
                          gridColumn="1"
                          gridRow={{ base: "1 / span 2", md: "1" }}
                          lineHeight={1}
                          aria-hidden
                        >
                          {index + 1}
                        </Text>
                        <Text
                          as="span"
                          textStyle="body.sm"
                          fontWeight={600}
                          color="text.primary"
                          noOfLines={isOpen ? undefined : 1}
                          gridColumn="2"
                          gridRow="1"
                        >
                          {item.description}
                        </Text>
                        <Box
                          as="span"
                          display="block"
                          h="10px"
                          w="full"
                          bg="sand.100"
                          borderRadius="full"
                          overflow="hidden"
                          gridColumn={{ base: "2 / span 2", md: "3" }}
                          gridRow={{ base: "2", md: "1" }}
                          aria-hidden
                        >
                          <MotionBox
                            as="span"
                            display="block"
                            h="full"
                            minW="2px"
                            bg={isOpen ? "navy.900" : "leaf.500"}
                            borderRadius="full"
                            _groupHover={{ bg: "navy.900" }}
                            sx={{ transitionProperty: "background-color", transitionDuration: "160ms" }}
                            initial={reduce ? false : { width: 0 }}
                            animate={{ width: `${share}%` }}
                            transition={
                              reduce
                                ? reducedTransition
                                : { duration: 0.6, ease: ease.standard, delay: index * 0.04 }
                            }
                          />
                        </Box>
                        <Text
                          as="span"
                          textStyle="numeral"
                          fontSize="0.875rem"
                          color="text.secondary"
                          textAlign="right"
                          whiteSpace="nowrap"
                          gridColumn={{ base: "3", md: "4" }}
                          gridRow="1"
                          aria-hidden
                        >
                          {formatCompact(item[period])}
                        </Text>
                        <Icon
                          as={FiChevronDown}
                          boxSize="16px"
                          color={isOpen ? "navy.900" : "sand.400"}
                          gridColumn={{ base: "4", md: "5" }}
                          gridRow="1"
                          transform={isOpen ? "rotate(180deg)" : undefined}
                          transition="transform 200ms cubic-bezier(0.2,0.8,0.2,1), color 160ms"
                          _groupHover={{ color: "navy.900" }}
                          aria-hidden
                        />
                      </Grid>
                      <VisuallyHidden>{`, ${withCurrency(item[period])}`}</VisuallyHidden>
                    </chakra.button>
                  </Tooltip>

                  {/* Breakdown for the clicked item: every period, its share of spend and a monthly average */}
                  <Collapse
                    in={isOpen}
                    animateOpacity
                    transition={reduce ? { enter: { duration: 0 }, exit: { duration: 0 } } : undefined}
                  >
                    <Box id={panelId} mx={2} mt={1} mb={3} p={4} layerStyle="cardMuted">
                      <SimpleGrid columns={{ base: 1, sm: 3 }} gap={3}>
                        {PERIODS.map(({ key, label }) => (
                          <Box key={key}>
                            <Text textStyle="label" color="text.muted">
                              {label}
                            </Text>
                            <Text
                              textStyle="numeral"
                              fontSize="1rem"
                              mt={1}
                              fontWeight={key === period ? 700 : 600}
                              color={key === period ? "text.primary" : "text.secondary"}
                            >
                              {withCurrency(item[key])}
                            </Text>
                          </Box>
                        ))}
                      </SimpleGrid>
                      <Flex mt={4} gap={3} wrap="wrap" align="center" justify="space-between">
                        <Text textStyle="caption" color="text.muted">
                          Item code {item.item_name} ·{" "}
                          {formatShare(periodTotal > 0 ? (item[period] / periodTotal) * 100 : 0)} of your{" "}
                          {periodLabel?.toLowerCase()} spend · about {withCurrency(item.last_6_month_sale / 6)} a
                          month over the last 6 months
                        </Text>
                        <Button
                          variant="link"
                          size="sm"
                          rightIcon={<FiArrowDown />}
                          minH={{ base: "44px", md: "auto" }}
                          onClick={() => showInTable(item)}
                        >
                          Show in table
                        </Button>
                      </Flex>
                    </Box>
                  </Collapse>
                </MotionBox>
              );
            })}
          </Box>
        )}

        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          gap={3}
          mt={8}
          mb={3}
        >
          {/* Programmatic focus target for "Show in table" (not in the tab order) */}
          <Box ref={allItemsRef} tabIndex={-1} scrollMarginTop="88px" _focus={{ outline: "none" }}>
            <Text as="h3" textStyle="h3" color="text.primary">
              All items
            </Text>
            <Text textStyle="caption" color="text.muted">
              Sorted by {periodLabel?.toLowerCase()} spend
              {searchTerm.trim() && ` · matching "${searchTerm.trim()}"`}
            </Text>
          </Box>
          <SearchField
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search items"
            aria-label="Search spend items"
          />
        </Flex>

        {currentItems.length === 0 ? (
          <EmptyState
            icon={FiSearch}
            title="No items match your search"
            description="Try another item name or code."
            py={8}
          />
        ) : (
          <DataTable size="sm" label="All items by spend">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th scope="col">Item</Th>
                  {PERIODS.map(({ key, label }) => (
                    <Th
                      key={key}
                      scope="col"
                      isNumeric
                      aria-sort={key === period ? "descending" : "none"}
                      color={key === period ? "text.primary" : undefined}
                    >
                      {/* The table is sorted by the selected period: a header click selects that period */}
                      <chakra.button
                        type="button"
                        onClick={() => setPeriod(key)}
                        display="inline-flex"
                        position="relative"
                        alignItems="center"
                        gap={1}
                        fontFamily="inherit"
                        fontSize="inherit"
                        fontWeight="inherit"
                        letterSpacing="inherit"
                        textTransform="inherit"
                        color="inherit"
                        cursor="pointer"
                        borderRadius="sm"
                        data-group
                        _hover={{ color: "text.primary" }}
                        _focusVisible={{ boxShadow: "outline", outline: "none" }}
                        // Larger hit area within the th's padding, without changing the layout
                        _before={{ content: '""', position: "absolute", inset: "-10px -6px" }}
                      >
                        {/* Arrow first, so the label stays flush right with the column's figures */}
                        <Icon
                          as={FiArrowDown}
                          boxSize="12px"
                          opacity={key === period ? 1 : 0}
                          transition="opacity 160ms"
                          _groupHover={{ opacity: key === period ? 1 : 0.5 }}
                          aria-hidden
                        />
                        {columnLabel(label)}
                      </chakra.button>
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {currentItems.map((item) => (
                  <Tr key={item.item_id}>
                    <Td whiteSpace="normal" minW="220px">
                      <Text fontWeight={600} color="text.primary">
                        {item.description}
                      </Text>
                      <Text textStyle="caption" color="text.muted">
                        {item.item_name}
                      </Text>
                    </Td>
                    {PERIODS.map(({ key }) => (
                      <Td
                        key={key}
                        isNumeric
                        // The theme's numeric-cell selector sets weight 600 and outranks a plain prop; sx replaces that
                        // block shallowly, so textAlign is repeated
                        sx={{ "&[data-is-numeric=true]": { textAlign: "end", fontWeight: key === period ? 700 : 600 } }}
                        color={key === period ? "text.primary" : "text.secondary"}
                      >
                        {formatAmount(item[key])}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </DataTable>
        )}

        {filteredItems.length > 0 && (
          <Pagination
            page={currentPage}
            pageSize={ITEMS_PER_PAGE}
            total={filteredItems.length}
            onChange={setPage}
          />
        )}
      </>
    );
  };

  return (
    <SectionCard
      folio="03"
      title="Spend analysis"
      lede={currency ? `What you spent per item, in ${currency}` : "What you spent per item"}
      isLoading={isPending}
      toolbar={
        hasItems ? (
          <SegmentedControl
            aria-label="Spend period"
            layoutId="spend-segment"
            size={segmentSize}
            value={period}
            onChange={setPeriod}
            options={PERIODS.map(({ key, label }) => ({ value: key, label }))}
            w={{ base: "100%", md: "auto" }}
            // 44px touch target on phones; the group selector outranks the Button's own height class
            sx={{ "& > button": { flex: { base: 1, md: "0 0 auto" }, h: { base: "2.75rem", md: "2.25rem" } } }}
          />
        ) : undefined
      }
    >
      {renderContent()}
    </SectionCard>
  );
};

export default SpendAnalysis;
